import { useId } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OFFERING_NUDGE_COPY } from "@/lib/offeringNudge";

/**
 * SCRUM-346 / ADR-165: persistent dashboard nudge to create a first offering. Presentational only;
 * visibility is decided by `useOfferingNudgeBanner`. Deliberately not dismissible, and the pulse is
 * continuous (no iteration count) but motion-safe only.
 */
export function OfferingNudgeBanner({ createPath = "/create-offering" }: { createPath?: string }) {
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className="relative rounded-xl border border-[#DCDDEE] bg-[#EEEFF9] px-4 py-3"
    >
      <span
        aria-hidden="true"
        data-testid="offering-nudge-pulse"
        className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-[#262A7A]/40 motion-safe:animate-pulse"
      />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-3">
        <Sparkles className="w-5 h-5 shrink-0 text-[#262A7A]" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <h2 id={headingId} className="text-sm font-semibold text-[#262A7A]">
            {OFFERING_NUDGE_COPY.bannerTitle}
          </h2>
          <p className="text-sm text-[#5A5E80]">{OFFERING_NUDGE_COPY.bannerText}</p>
        </div>
        <Button
          asChild
          size="sm"
          className="cursor-pointer gap-2 bg-[#262A7A] transition-colors duration-200 hover:bg-[#363A96]"
        >
          <Link to={createPath}>
            {OFFERING_NUDGE_COPY.primaryCta}
            <ArrowRight aria-hidden="true" className="w-4 h-4" />
          </Link>
        </Button>
      </div>
    </section>
  );
}
